<template>
  <div class="auth-page">
    <div class="auth-card">
      <AuthPageLogo class="auth-card__logo" />
      <h1 class="auth-card__title">{{ titleByMode }}</h1>
      <p class="auth-card__subtitle">
        {{ subtitleByMode }}
      </p>

      <!-- Обычная форма входа -->
      <template v-if="authMode === 'login'">
        <form class="auth-form" @submit.prevent="handleLoginSubmit">
          <label class="auth-form__label" for="login">Логин или email</label>
          <input
            id="login"
            v-model.trim="form.login"
            class="auth-form__input"
            type="text"
            autocomplete="username"
            placeholder="Введите логин или email"
            :disabled="isSubmitting"
          />

          <label class="auth-form__label" for="password">Пароль</label>
          <input
            id="password"
            v-model="form.password"
            class="auth-form__input"
            type="password"
            autocomplete="current-password"
            placeholder="Введите пароль"
            :disabled="isSubmitting"
          />

          <p v-if="errorMessage" class="auth-form__error">
            <span>{{ errorMessage }}</span>
          </p>

          <BaseButton
            text="Войти"
            styles="primary-gradient"
            full-width
            :is-disabled="isLoginSubmitDisabled"
            vibration="critical"
            @on-submit="handleLoginSubmit"
          />
        </form>

        <div v-if="!isTelegramApp" class="auth-card__actions">
          <p class="auth-card__link">
            <button
              type="button"
              class="auth-card__link-button"
              @click="switchToRegister"
            >
              Зарегистрироваться
            </button>
          </p>
          <p class="auth-card__link">
            <button
              type="button"
              class="auth-card__link-button"
              @click="switchToRestoreStep1"
            >
              Забыли пароль?
            </button>
          </p>
        </div>
        <p v-else class="auth-card__hint">
          Нет логина? Откройте приложение в Telegram Web App и установите
          учетные данные в разделе &laquo;Настройки входа&raquo;.
        </p>
      </template>

      <!-- Регистрация: шаг 1 — email и пароль -->
      <template v-else-if="authMode === 'register'">
        <form class="auth-form" @submit.prevent="handleRegisterSubmit">
          <label class="auth-form__label" for="register-email">Email</label>
          <input
            id="register-email"
            v-model.trim="registration.email"
            class="auth-form__input"
            type="email"
            autocomplete="email"
            placeholder="Введите email"
            :disabled="isSubmitting"
          />

          <label class="auth-form__label" for="register-password">Пароль</label>
          <input
            id="register-password"
            v-model="registration.password"
            class="auth-form__input"
            type="password"
            autocomplete="new-password"
            placeholder="От 8 до 32 символов"
            :disabled="isSubmitting"
          />

          <label class="auth-form__label" for="register-confirm-password"
            >Повторите пароль</label
          >
          <input
            id="register-confirm-password"
            v-model="registration.confirmPassword"
            class="auth-form__input"
            type="password"
            autocomplete="new-password"
            placeholder="Повторите пароль"
            :disabled="isSubmitting"
          />

          <p v-if="errorMessage" class="auth-form__error">{{ errorMessage }}</p>
          <BaseButton
            text="Зарегистрироваться"
            styles="primary-gradient"
            full-width
            :is-disabled="isRegisterSubmitDisabled"
            vibration="critical"
            @on-submit="handleRegisterSubmit"
          />
          <button type="button" class="auth-card__back" @click="backToLogin">
            Назад ко входу
          </button>
        </form>
      </template>

      <!-- Регистрация: шаг 2 — подтверждение email -->
      <template v-else-if="authMode === 'register-verify'">
        <form class="auth-form" @submit.prevent="handleRegisterVerifySubmit">
          <div
            class="auth-otp"
            role="group"
            aria-labelledby="register-code-label"
          >
            <input
              v-for="(_, index) in registerCodeDigits"
              :id="`register-code-${index}`"
              :key="index"
              :ref="(element) => setRegisterCodeInputRef(element, index)"
              class="auth-otp__input"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              :autocomplete="index === 0 ? 'one-time-code' : 'off'"
              :aria-label="`Цифра ${index + 1} из ${REGISTER_CODE_LENGTH}`"
              :value="registerCodeDigits[index]"
              :maxlength="REGISTER_CODE_LENGTH"
              enterkeyhint="done"
              :disabled="isSubmitting"
              @focus="handleRegisterCodeFocus"
              @input="handleRegisterCodeInput($event, index)"
              @keydown="handleRegisterCodeKeydown($event, index)"
              @paste="handleRegisterCodePaste($event, index)"
            />
          </div>
          <p v-if="errorMessage" class="auth-form__error">{{ errorMessage }}</p>
          <p v-if="successMessage" class="auth-form__success">
            {{ successMessage }}
          </p>
          <BaseButton
            text="Подтвердить email"
            styles="primary-gradient"
            full-width
            :is-disabled="isRegisterVerifySubmitDisabled"
            vibration="critical"
            @on-submit="handleRegisterVerifySubmit"
          />
          <p v-if="isRegisterRetryCooldownActive" class="auth-card__timer">
            Отправить еще раз можно через {{ registerRetryCooldownLabel }}
          </p>
          <BaseButton
            v-else
            text="Отправить еще раз"
            styles="secondary-200"
            full-width
            :is-disabled="isSubmitting"
            vibration="button"
            @click.prevent.stop
            @on-submit="handleRegisterVerifyRetry"
          />
          <button
            type="button"
            class="auth-card__back"
            @click="returnToRegisterStep"
          >
            Изменить email
          </button>
          <button type="button" class="auth-card__back" @click="backToLogin">
            Назад ко входу
          </button>
        </form>
      </template>

      <!-- Восстановление пароля: шаг 1 — identifier -->
      <template v-else-if="authMode === 'restore-step1'">
        <form class="auth-form" @submit.prevent="handleRestoreStep1">
          <label class="auth-form__label" for="restore-identifier"
            >Логин или email</label
          >
          <input
            id="restore-identifier"
            v-model.trim="restore.identifier"
            class="auth-form__input"
            type="text"
            autocomplete="username"
            placeholder="Введите логин или email"
            :disabled="isSubmitting"
          />
          <p
            v-if="errorMessage"
            class="auth-form__error"
            :class="{ 'auth-form__error--warning': showRestoreTelegramCta }"
          >
            {{ errorMessage }}
          </p>
          <BaseButton
            v-if="showRestoreTelegramCta"
            text="Перейти в Telegram"
            styles="primary-gradient"
            full-width
            :is-disabled="isSubmitting"
            vibration="success"
            @on-submit="openTelegramRegisterWebApp"
          />
          <BaseButton
            v-else
            text="Отправить код"
            styles="primary-gradient"
            full-width
            :is-disabled="!restore.identifier.trim() || isSubmitting"
            vibration="critical"
            @on-submit="handleRestoreStep1"
          />
          <button type="button" class="auth-card__back" @click="backToLogin">
            Назад ко входу
          </button>
        </form>
      </template>

      <!-- Восстановление пароля: шаг 2 — код из email -->
      <template v-else-if="authMode === 'restore-step2'">
        <form class="auth-form" @submit.prevent="handleRestoreStep2">
          <p v-if="restore.maskedEmail" class="auth-card__masked-email">
            Код отправлен на {{ restore.maskedEmail }}
          </p>
          <label class="auth-form__label" for="restore-code"
            >Код из письма</label
          >
          <input
            id="restore-code"
            v-model.trim="restore.code"
            class="auth-form__input"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            placeholder="6 цифр"
            maxlength="6"
            :disabled="isSubmitting"
          />
          <p v-if="errorMessage" class="auth-form__error">{{ errorMessage }}</p>
          <BaseButton
            text="Проверить код"
            styles="primary-gradient"
            full-width
            :is-disabled="isRestoreStep2Disabled"
            vibration="critical"
            @on-submit="handleRestoreStep2"
          />
          <button type="button" class="auth-card__back" @click="backToLogin">
            Назад ко входу
          </button>
        </form>
      </template>

      <!-- Восстановление пароля: шаг 3 — новый пароль -->
      <template v-else-if="authMode === 'restore-step3'">
        <form class="auth-form" @submit.prevent="handleRestoreStep3">
          <label class="auth-form__label" for="restore-new-password"
            >Новый пароль</label
          >
          <input
            id="restore-new-password"
            v-model="restore.newPassword"
            class="auth-form__input"
            type="password"
            autocomplete="new-password"
            placeholder="От 8 до 32 символов"
            :disabled="isSubmitting"
          />
          <label class="auth-form__label" for="restore-confirm-password"
            >Повторите пароль</label
          >
          <input
            id="restore-confirm-password"
            v-model="restore.confirmPassword"
            class="auth-form__input"
            type="password"
            autocomplete="new-password"
            placeholder="Повторите новый пароль"
            :disabled="isSubmitting"
          />
          <p v-if="errorMessage" class="auth-form__error">{{ errorMessage }}</p>
          <BaseButton
            text="Сохранить пароль"
            styles="primary-gradient"
            full-width
            :is-disabled="isRestoreStep3Disabled"
            vibration="critical"
            @on-submit="handleRestoreStep3"
          />
          <button type="button" class="auth-card__back" @click="backToLogin">
            Назад ко входу
          </button>
        </form>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, nextTick, onMounted, onUnmounted } from "vue";
import type { ComponentPublicInstance } from "vue";
import { useAuth } from "@/shared/lib/auth/useAuth";
import BaseButton from "@/ui/components/Base/BaseButton.vue";
import AuthPageLogo from "@/assets/icons/auth-page-logo.svg";
import { SentryService } from "@/shared/lib/sentry/sentryService";
import { TELEGRAM_REGISTER_WEBAPP_URL } from "@/shared/config/browserLinks";
import { isTelegramSession } from "@/shared/lib/telegram/runtime";

const emit = defineEmits<{
  (e: "login-success"): void;
}>();

type AuthMode =
  | "login"
  | "register"
  | "register-verify"
  | "restore-step1"
  | "restore-step2"
  | "restore-step3";

const {
  loginWithCredentials,
  registerWithEmail,
  verifyRegistrationEmail,
  retryRegistrationEmailVerification,
  restorePassword,
  verifyRestoreCode,
  setRestoredPassword,
} = useAuth();

const form = reactive({
  login: "",
  password: "",
});

const authMode = ref<AuthMode>("login");
const isSubmitting = ref(false);
const errorMessage = ref("");
const successMessage = ref("");
const showRestoreTelegramCta = ref(false);
const REGISTER_CODE_LENGTH = 6;
const REGISTER_RETRY_COOLDOWN_SECONDS = 60;
const registerCodeDigits = ref<string[]>(
  Array.from({ length: REGISTER_CODE_LENGTH }, () => ""),
);
const registerRetryCooldownSecondsLeft = ref(0);
let registerRetryCooldownInterval: number | null = null;
const registerCodeInputRefs = ref<HTMLInputElement[]>([]);

const restore = reactive({
  identifier: "",
  code: "",
  resetSecret: "",
  maskedEmail: "",
  newPassword: "",
  confirmPassword: "",
});

const registration = reactive({
  email: "",
  password: "",
  confirmPassword: "",
  code: "",
});

const isTelegramApp = computed(() => isTelegramSession());

const titleByMode = computed(() => {
  if (authMode.value === "login") return "Вход в аккаунт";
  if (authMode.value === "register") return "Регистрация";
  if (authMode.value === "register-verify") return "Подтверждение email";
  if (authMode.value === "restore-step1") return "Восстановление пароля";
  if (authMode.value === "restore-step2") return "Код из письма";
  if (authMode.value === "restore-step3") return "Новый пароль";
  return "Вход в аккаунт";
});

const subtitleByMode = computed(() => {
  if (authMode.value === "login") {
    return isTelegramApp.value
      ? "Используйте логин и пароль, которые вы установили в Telegram Web App."
      : "Войдите по логину или email, либо создайте аккаунт на сайте.";
  }
  if (authMode.value === "register") {
    return "Укажите email и пароль — мы отправим код подтверждения.";
  }
  if (authMode.value === "register-verify") {
    return "Введите 6-значный код из письма.";
  }
  if (authMode.value === "restore-step1") {
    return "Введите логин или email — мы отправим код на почту.";
  }
  if (authMode.value === "restore-step2") {
    return "Введите 6-значный код из письма.";
  }
  if (authMode.value === "restore-step3") {
    return "Придумайте новый пароль от 8 до 32 символов.";
  }
  return "";
});

const isLoginSubmitDisabled = computed(() => {
  return !form.login || !form.password || isSubmitting.value;
});

const isRegisterSubmitDisabled = computed(() => {
  const password = registration.password;
  const validLength = password.length >= 8 && password.length <= 32;
  return (
    !isValidEmail(registration.email) ||
    !validLength ||
    password !== registration.confirmPassword ||
    isSubmitting.value
  );
});

const isRegisterVerifySubmitDisabled = computed(() => {
  return !/^\d{6}$/.test(registration.code) || isSubmitting.value;
});

const isRegisterRetryCooldownActive = computed(() => {
  return registerRetryCooldownSecondsLeft.value > 0;
});

const registerRetryCooldownLabel = computed(() => {
  const secondsLeft = Math.max(0, registerRetryCooldownSecondsLeft.value);
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
});

const isRestoreStep2Disabled = computed(() => {
  return restore.code.length !== 6 || isSubmitting.value;
});

const isRestoreStep3Disabled = computed(() => {
  const p = restore.newPassword;
  const c = restore.confirmPassword;
  const validLength = p.length >= 8 && p.length <= 32;
  return !validLength || p !== c || isSubmitting.value;
});

function normalizeApiError(error: unknown): string {
  const fallback = "Не удалось выполнить действие. Попробуйте ещё раз.";
  if (error && typeof error === "object" && "data" in error) {
    const d = (error as { data?: any }).data;
    if (d?.message && typeof d.message === "string") return d.message;
    if (typeof d?.detail === "string" && d.detail) return d.detail;
    if (Array.isArray(d?.detail) && d.detail.length) {
      const msgs = d.detail.map((x: { msg?: string }) => x.msg).filter(Boolean);
      if (msgs.length) return msgs.join(", ");
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function sanitizeRegisterCode(value: string): string {
  return value.replace(/\D/g, "").slice(0, REGISTER_CODE_LENGTH);
}

function syncRegisterCodeFromDigits() {
  registration.code = registerCodeDigits.value.join("");
}

function clampRegisterCodeIndex(index: number): number {
  return Math.min(Math.max(index, 0), REGISTER_CODE_LENGTH - 1);
}

function setRegisterCodeInputRef(
  element: Element | ComponentPublicInstance | null,
  index: number,
) {
  if (element instanceof HTMLInputElement) {
    registerCodeInputRefs.value[index] = element;
  } else {
    delete registerCodeInputRefs.value[index];
  }
}

function focusRegisterCodeInput(index = 0) {
  if (authMode.value !== "register-verify") return;
  void nextTick(() => {
    const input = registerCodeInputRefs.value[clampRegisterCodeIndex(index)];
    input?.focus();
    input?.select();
  });
}

function clearRegistrationCode(options: { focus?: boolean } = {}) {
  registerCodeDigits.value = Array.from(
    { length: REGISTER_CODE_LENGTH },
    () => "",
  );
  syncRegisterCodeFromDigits();
  if (options.focus) focusRegisterCodeInput(0);
}

function updateRegisterCodeDigit(index: number, digit: string) {
  const nextDigits = [...registerCodeDigits.value];
  nextDigits[index] = digit;
  registerCodeDigits.value = nextDigits;
  syncRegisterCodeFromDigits();
}

function applyRegisterCodeValue(value: string, startIndex: number) {
  const digits = sanitizeRegisterCode(value);
  if (!digits) return;

  const fillsWholeCode = digits.length >= REGISTER_CODE_LENGTH;
  const targetStartIndex = fillsWholeCode
    ? 0
    : clampRegisterCodeIndex(startIndex);
  const nextDigits = fillsWholeCode
    ? Array.from({ length: REGISTER_CODE_LENGTH }, () => "")
    : [...registerCodeDigits.value];

  for (let i = 0; i < digits.length; i += 1) {
    const targetIndex = targetStartIndex + i;
    if (targetIndex >= REGISTER_CODE_LENGTH) break;
    nextDigits[targetIndex] = digits[i];
  }

  registerCodeDigits.value = nextDigits;
  syncRegisterCodeFromDigits();

  const lastFilledIndex = Math.min(
    targetStartIndex + digits.length - 1,
    REGISTER_CODE_LENGTH - 1,
  );
  const nextFocusIndex = fillsWholeCode
    ? REGISTER_CODE_LENGTH - 1
    : Math.min(lastFilledIndex + 1, REGISTER_CODE_LENGTH - 1);
  focusRegisterCodeInput(nextFocusIndex);
}

function handleRegisterCodeFocus(event: FocusEvent) {
  if (event.target instanceof HTMLInputElement) {
    event.target.select();
  }
}

function handleRegisterCodeInput(event: Event, index: number) {
  if (!(event.target instanceof HTMLInputElement)) return;

  const digits = sanitizeRegisterCode(event.target.value);
  if (digits.length > 1) {
    applyRegisterCodeValue(digits, index);
    return;
  }

  if (!digits) {
    event.target.value = registerCodeDigits.value[index] ?? "";
    return;
  }

  updateRegisterCodeDigit(index, digits);
  if (index < REGISTER_CODE_LENGTH - 1) {
    focusRegisterCodeInput(index + 1);
  }
}

function handleRegisterCodeKeydown(event: KeyboardEvent, index: number) {
  if (event.key === "Backspace") {
    event.preventDefault();
    if (registerCodeDigits.value[index]) {
      updateRegisterCodeDigit(index, "");
      focusRegisterCodeInput(index);
    } else if (index > 0) {
      updateRegisterCodeDigit(index - 1, "");
      focusRegisterCodeInput(index - 1);
    }
    return;
  }

  if (event.key === "Delete") {
    event.preventDefault();
    updateRegisterCodeDigit(index, "");
    focusRegisterCodeInput(index);
    return;
  }

  if (event.key === "ArrowLeft") {
    event.preventDefault();
    focusRegisterCodeInput(index - 1);
    return;
  }

  if (event.key === "ArrowRight") {
    event.preventDefault();
    focusRegisterCodeInput(index + 1);
    return;
  }

  const hasShortcutModifier = event.metaKey || event.ctrlKey || event.altKey;
  if (
    !hasShortcutModifier &&
    event.key.length === 1 &&
    !/^\d$/.test(event.key)
  ) {
    event.preventDefault();
  }
}

function handleRegisterCodePaste(event: ClipboardEvent, index: number) {
  const pastedText = event.clipboardData?.getData("text") ?? "";
  const digits = sanitizeRegisterCode(pastedText);
  event.preventDefault();
  if (digits) {
    applyRegisterCodeValue(digits, index);
  }
}

function isMissingEmailForRestoreError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { status?: number; data?: any };
  if (e.status !== 400 || !e.data) return false;
  const detail = e.data.detail;
  if (typeof detail === "string") {
    return detail === "У пользователя не указан email в профиле";
  }
  return false;
}

function openTelegramRegisterWebApp() {
  const url = TELEGRAM_REGISTER_WEBAPP_URL;
  const tg = (window as any).Telegram?.WebApp;
  if (tg && typeof tg.openLink === "function") {
    tg.openLink(url);
  } else {
    window.open(url, "_blank");
  }
}

function clearRestoreState() {
  restore.identifier = "";
  restore.code = "";
  restore.resetSecret = "";
  restore.maskedEmail = "";
  restore.newPassword = "";
  restore.confirmPassword = "";
  errorMessage.value = "";
  successMessage.value = "";
  showRestoreTelegramCta.value = false;
}

function stopRegisterRetryCooldown() {
  if (registerRetryCooldownInterval !== null) {
    window.clearInterval(registerRetryCooldownInterval);
    registerRetryCooldownInterval = null;
  }
}

function resetRegisterRetryCooldown() {
  stopRegisterRetryCooldown();
  registerRetryCooldownSecondsLeft.value = 0;
}

function startRegisterRetryCooldown() {
  stopRegisterRetryCooldown();
  registerRetryCooldownSecondsLeft.value = REGISTER_RETRY_COOLDOWN_SECONDS;
  registerRetryCooldownInterval = window.setInterval(() => {
    registerRetryCooldownSecondsLeft.value -= 1;
    if (registerRetryCooldownSecondsLeft.value <= 0) {
      resetRegisterRetryCooldown();
    }
  }, 1000);
}

function clearRegistrationState() {
  registration.email = "";
  registration.password = "";
  registration.confirmPassword = "";
  clearRegistrationCode();
  errorMessage.value = "";
  successMessage.value = "";
  resetRegisterRetryCooldown();
}

function backToLogin() {
  SentryService.trackButtonClick("Назад ко входу", {
    from_step: authMode.value,
  });
  clearRestoreState();
  clearRegistrationState();
  authMode.value = "login";
}

function switchToRegister() {
  if (isTelegramApp.value) return;
  SentryService.trackButtonClick("Зарегистрироваться", {
    flow: "email_registration",
  });
  clearRegistrationCode();
  errorMessage.value = "";
  successMessage.value = "";
  showRestoreTelegramCta.value = false;
  resetRegisterRetryCooldown();
  authMode.value = "register";
}

function returnToRegisterStep() {
  clearRegistrationCode();
  errorMessage.value = "";
  successMessage.value = "";
  resetRegisterRetryCooldown();
  authMode.value = "register";
}

function switchToRestoreStep1() {
  if (isTelegramApp.value) return;
  SentryService.trackButtonClick("Забыли пароль?", {
    flow: "password_restore",
  });
  clearRestoreState();
  errorMessage.value = "";
  successMessage.value = "";
  authMode.value = "restore-step1";
}

const handleLoginSubmit = async () => {
  if (isLoginSubmitDisabled.value) return;
  isSubmitting.value = true;
  errorMessage.value = "";
  successMessage.value = "";
  try {
    await loginWithCredentials({
      login: form.login,
      password: form.password,
    });
    emit("login-success");
  } catch (error) {
    errorMessage.value = normalizeApiError(error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleRegisterSubmit = async () => {
  if (isRegisterSubmitDisabled.value || isTelegramApp.value) return;
  isSubmitting.value = true;
  errorMessage.value = "";
  successMessage.value = "";
  registration.email = registration.email.trim();
  try {
    const res = await registerWithEmail({
      email: registration.email,
      password: registration.password,
    });
    if (res.success) {
      clearRegistrationCode();
      authMode.value = "register-verify";
      successMessage.value = res.message || "Код отправлен на email.";
      startRegisterRetryCooldown();
      focusRegisterCodeInput(0);
      SentryService.trackUserAction("Регистрация: код отправлен", {
        flow: "email_registration",
      });
    } else {
      errorMessage.value = res.message || "Не удалось отправить код.";
    }
  } catch (error) {
    SentryService.trackError(
      error instanceof Error ? error : new Error(String(error)),
      { step: 1 },
      "email_registration_step_1",
    );
    errorMessage.value = normalizeApiError(error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleRegisterVerifySubmit = async () => {
  if (isRegisterVerifySubmitDisabled.value || isTelegramApp.value) return;
  isSubmitting.value = true;
  errorMessage.value = "";
  successMessage.value = "";
  try {
    await verifyRegistrationEmail({
      email: registration.email.trim(),
      code: registration.code,
    });
    SentryService.trackUserAction("Регистрация: email подтвержден, автологин", {
      flow: "email_registration",
    });
    resetRegisterRetryCooldown();
    clearRegistrationState();
    emit("login-success");
  } catch (error) {
    SentryService.trackError(
      error instanceof Error ? error : new Error(String(error)),
      { step: 2 },
      "email_registration_step_2",
    );
    errorMessage.value = normalizeApiError(error);
    clearRegistrationCode({ focus: true });
  } finally {
    isSubmitting.value = false;
  }
};

const handleRegisterVerifyRetry = async () => {
  if (
    isSubmitting.value ||
    isRegisterRetryCooldownActive.value ||
    isTelegramApp.value
  )
    return;
  isSubmitting.value = true;
  errorMessage.value = "";
  successMessage.value = "";
  try {
    const res = await retryRegistrationEmailVerification({
      email: registration.email.trim(),
    });
    if (res.success) {
      clearRegistrationCode({ focus: true });
      successMessage.value = res.message || "Код отправлен еще раз.";
      startRegisterRetryCooldown();
      SentryService.trackUserAction("Регистрация: код отправлен повторно", {
        flow: "email_registration",
      });
    } else {
      errorMessage.value = res.message || "Не удалось отправить код повторно.";
    }
  } catch (error) {
    SentryService.trackError(
      error instanceof Error ? error : new Error(String(error)),
      { step: "retry" },
      "email_registration_verify_retry",
    );
    errorMessage.value = normalizeApiError(error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleRestoreStep1 = async () => {
  if (!restore.identifier.trim() || isSubmitting.value) return;
  isSubmitting.value = true;
  errorMessage.value = "";
  successMessage.value = "";
  showRestoreTelegramCta.value = false;
  try {
    const res = await restorePassword({
      identifier: restore.identifier.trim(),
    });
    if (res.success) {
      restore.maskedEmail = res.masked_email ?? "";
      authMode.value = "restore-step2";
      SentryService.trackUserAction("Восстановление пароля: код отправлен", {
        step: 1,
        has_masked_email: !!res.masked_email,
      });
    } else {
      errorMessage.value = res.message || "Не удалось отправить код.";
    }
  } catch (error) {
    SentryService.trackError(
      error instanceof Error ? error : new Error(String(error)),
      { step: 1 },
      "password_restore_step_1",
    );
    if (isMissingEmailForRestoreError(error)) {
      errorMessage.value =
        "У вас не заполнены необходимые данные для восстановления пароля. \nКликните на кнопку ниже, чтобы заполнить их";
      showRestoreTelegramCta.value = true;
    } else {
      errorMessage.value = normalizeApiError(error);
      showRestoreTelegramCta.value = false;
    }
  } finally {
    isSubmitting.value = false;
  }
};

const handleRestoreStep2 = async () => {
  if (restore.code.length !== 6 || isSubmitting.value) return;
  isSubmitting.value = true;
  errorMessage.value = "";
  successMessage.value = "";
  try {
    const res = await verifyRestoreCode({
      identifier: restore.identifier.trim(),
      code: restore.code,
    });
    if (res.success && res.reset_secret) {
      restore.resetSecret = res.reset_secret;
      authMode.value = "restore-step3";
      SentryService.trackUserAction("Восстановление пароля: код проверен", {
        step: 2,
      });
    } else {
      errorMessage.value =
        res.message || "Неверный или истёкший код. Запросите новый.";
      restore.code = "";
    }
  } catch (error) {
    SentryService.trackError(
      error instanceof Error ? error : new Error(String(error)),
      { step: 2 },
      "password_restore_step_2",
    );
    errorMessage.value = normalizeApiError(error);
    restore.code = "";
  } finally {
    isSubmitting.value = false;
  }
};

const handleRestoreStep3 = async () => {
  const p = restore.newPassword;
  if (
    p.length < 8 ||
    p.length > 32 ||
    p !== restore.confirmPassword ||
    isSubmitting.value
  )
    return;
  if (!restore.resetSecret) {
    errorMessage.value = "Сессия восстановления истекла. Начните заново.";
    backToLogin();
    return;
  }
  isSubmitting.value = true;
  errorMessage.value = "";
  successMessage.value = "";
  try {
    const res = await setRestoredPassword({
      identifier: restore.identifier.trim(),
      reset_secret: restore.resetSecret,
      new_password: p,
    });
    if (res.success) {
      try {
        await loginWithCredentials({
          login: restore.identifier.trim(),
          password: p,
        });
        SentryService.trackUserAction(
          "Восстановление пароля: пароль изменён, автологин",
          { step: 3, autologin: true },
        );
        clearRestoreState();
        emit("login-success");
      } catch (loginErr) {
        SentryService.trackUserAction(
          "Восстановление пароля: пароль изменён, автологин не выполнен",
          { step: 3, autologin: false },
        );
        errorMessage.value = "Пароль изменён. Войдите с новым паролем.";
        clearRestoreState();
        authMode.value = "login";
      }
    } else {
      errorMessage.value = res.message || "Не удалось сохранить пароль.";
    }
  } catch (error) {
    SentryService.trackError(
      error instanceof Error ? error : new Error(String(error)),
      { step: 3 },
      "password_restore_step_3",
    );
    errorMessage.value = normalizeApiError(error);
    backToLogin();
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  if (authMode.value !== "login") {
    clearRestoreState();
    clearRegistrationState();
  }
});

onUnmounted(() => {
  resetRegisterRetryCooldown();
});
</script>

<style lang="scss" scoped>
.auth-page {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: var(--app-header-safe-gap) 0 var(--app-footer-cta-gap);
}

.auth-card {
  max-width: 360px;
  width: 100%;
  background: rgba(10, 12, 15, 0.75);
  border-radius: 20px;
  padding: 32px 24px;
  box-sizing: border-box;
  backdrop-filter: blur(8px);
  text-align: center;
  color: $neutral-100;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: auto 0;

  &__logo {
    width: 120px;
    height: auto;
    margin: 0 auto 8px;
  }

  &__title {
    @include h1;
    margin: 0;
  }

  &__subtitle {
    @include p2-regular;
    margin: 0;
    color: $neutral-60;
  }

  &__hint {
    margin: 8px 0 0;
    @include p3-regular;
    font-size: 10px;
    color: $neutral-60;
  }

  &__link {
    margin: 0;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__link-button {
    background: none;
    border: none;
    padding: 0;
    color: $primary-100;
    @include p1-medium;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      opacity: 0.9;
    }
  }

  &__back {
    background: none;
    border: none;
    padding: 8px 0;
    color: $neutral-60;
    @include p2-medium;
    cursor: pointer;

    &:hover {
      color: $neutral-100;
    }
  }

  &__masked-email {
    @include p3-regular;
    color: $neutral-60;
    margin: 0 0 4px;
    text-align: left;
  }

  &__timer {
    @include p3-regular;
    color: $neutral-60;
    margin: 0;
    text-align: center;
  }
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;

  &__label {
    @include p2-medium;
    color: $neutral-100;
  }

  &__input {
    width: 100%;
    height: 48px;
    padding: 12px 16px;
    border-radius: 12px;
    border: 2px solid $secondary-200;
    background: $secondary-200;
    color: $neutral-100;
    font-size: 16px;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: $primary-100;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &::placeholder {
      color: $neutral-60;
    }
  }

  &__error {
    @include p3-regular;
    color: #ff4d4f;
    margin: 4px 0 0;
  }

  &__success {
    @include p3-regular;
    color: $primary-100;
    margin: 4px 0 0;
  }

  &__error--warning {
    color: #ffa940;
    font-size: 12px;
    white-space: break-spaces;
  }

  :deep(.button-base-block) {
    width: 100%;
  }

  :deep(.button-base.full-width) {
    width: 100%;
    min-height: 48px;
    padding: 12px 16px;
    box-sizing: border-box;
  }
}

.auth-otp {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
  width: 100%;

  &__input {
    width: 100%;
    min-width: 0;
    height: 48px;
    padding: 0;
    border-radius: 12px;
    border: 2px solid $secondary-200;
    background: $secondary-200;
    color: $neutral-100;
    font-size: 20px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    text-align: center;
    caret-color: $primary-100;
    transition:
      border-color 0.2s ease,
      background-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: $primary-100;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
